/**
 * Carbon tag (DESIGN.md "Chips"): a 24px full pill (18px for counts in tabs)
 * with 12px text. One of three files allowed `rounded-full`.
 *   default        layer-selected with primary text (deltas, change kinds, counts)
 *   cat            the pillar's tint with its ink tone (identification only)
 *   high-contrast  the strong neutral: a drifted value, a new drift
 *   outline        a 1px border-strong ring with secondary text (framework names)
 * `onClick` makes it a selectable tag (aria-pressed); `onDismiss` adds a close
 * button (a filter tag).
 */
import type { CSSProperties, ReactNode } from 'react'
import clsx from 'clsx'
import { IconClose } from './icons'
import type { Category, TagKind, TagSize } from './types'

export interface TagProps {
  kind?: TagKind
  /** Required for kind="cat": which pillar's tint. */
  category?: Category
  size?: TagSize
  /** Leading 12px icon. */
  icon?: ReactNode
  /** Filter tag: renders a close button that calls this. */
  onDismiss?: () => void
  /** The close button's accessible name. Default: "Remove {text}". */
  dismissLabel?: string
  /** Selectable tag state (with onClick). */
  selected?: boolean
  onClick?: () => void
  /** A selectable tag that can't be pressed now (its row is saving): no hover, placeholder text unless selected. */
  disabled?: boolean
  title?: string
  className?: string
  children: ReactNode
}

const KIND: Record<TagKind, string> = {
  default: 'bg-layer-selected text-text-primary',
  // Category tints mix with transparent in both themes, so the computed color keeps the pillar hue.
  cat: 'bg-[color:color-mix(in_oklab,var(--tag-c)_18%,transparent)] light:bg-[color:color-mix(in_oklab,var(--tag-c)_14%,transparent)] text-[color:var(--tag-ci)]',
  'high-contrast': 'bg-inverse font-semibold text-[color:var(--background)]',
  outline: 'bg-transparent text-text-secondary ring-1 ring-inset ring-line-strong',
}

export function Tag({
  kind = 'default',
  category,
  size = 'md',
  icon,
  onDismiss,
  dismissLabel,
  selected,
  onClick,
  disabled = false,
  title,
  className,
  children,
}: TagProps) {
  const style: CSSProperties | undefined =
    kind === 'cat' && category
      ? ({ '--tag-c': `var(--cat-${category})`, '--tag-ci': `var(--cat-${category}-ink)` } as CSSProperties)
      : undefined
  const selectable = !onDismiss && !!onClick
  const classes = clsx(
    'inline-flex max-w-full items-center gap-1 overflow-hidden whitespace-nowrap rounded-full align-middle',
    'text-[12px] font-normal leading-[1.33] tracking-[0.32px]',
    size === 'sm' ? 'h-[18px] px-1.5' : 'h-6 px-2',
    selectable && selected ? 'bg-inverse text-[color:var(--background)]' : selectable && disabled ? 'bg-layer-selected text-text-placeholder' : KIND[kind],
    selectable && !selected && !disabled && 'transition-colors duration-110 ease-carbon hover:bg-layer-hover',
    selectable && disabled && 'cursor-not-allowed',
    className,
  )
  const body = (
    <>
      {icon ? (
        <span aria-hidden="true" className="grid flex-none place-items-center [&>svg]:h-3 [&>svg]:w-3">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{children}</span>
    </>
  )

  if (onDismiss) {
    const name = dismissLabel ?? `Remove ${typeof children === 'string' ? children : 'filter'}`
    return (
      <span className={clsx(classes, 'pr-1')} style={style} title={title}>
        {body}
        <button
          type="button"
          aria-label={name}
          onClick={onDismiss}
          className="grid h-4 w-4 flex-none place-items-center rounded-full transition-colors duration-110 ease-carbon hover:bg-layer-hover"
        >
          <IconClose size={12} />
        </button>
      </span>
    )
  }
  if (selectable) {
    return (
      <button type="button" aria-pressed={!!selected} onClick={onClick} disabled={disabled || undefined} className={classes} style={style} title={title}>
        {body}
      </button>
    )
  }
  return (
    <span className={classes} style={style} title={title}>
      {body}
    </span>
  )
}

export default Tag
